/**
 * Générateur — Tester si une égalité entre expressions algébriques comportant
 * une variable est vraie ou fausse.
 * Attendu ID : 934 | Niveau : 5ème | Domaine : Nombres et calculs / Calcul littéral
 *
 * Plan de cartes (mise en conformité avec la spécification 934, campagne 5e,
 * 2026-09-22) :
 *
 *   Carte « A = B ? Tester pour deux valeurs » (id croiser) — niveaux 1 à 4
 *     croiser_texte_trou_saisie : un champ pour chaque membre, pour chacune
 *       des deux ou trois valeurs testées (type_reponse).
 *     croiser_qcm_unique : la conclusion, quatre propositions constantes.
 *
 *   Carte « Vrai ou faux pour la valeur indiquée » (id vf) — niveaux 1 et 2
 *     vf_tableau_vf : quatre égalités à éprouver, chacune pour une valeur
 *       donnée. Variante compacte du type_reponse (la conclusion seule).
 *
 *   Carte « Comparer deux expressions dans un tableau » (id compar) — niveaux 1 à 3
 *     compar_texte_trou_saisie : le tableau de valeurs des deux membres.
 *     compar_qcm_unique : la conclusion lue sur le tableau rempli.
 *
 * Spécification (C:\tanoed\Attendu-5e\specifications-tanoed\5e\934.json) :
 *   Bornes appliquées : UNE seule variable (borne `prescrit`) ; deux ou trois
 *   valeurs testées ; coefficients de la variable 1..9, toujours différents
 *   d'un membre à l'autre ; constantes −9..9, jamais nulles, positives aux
 *   niveaux 1 ; valeur qui vérifie l'égalité et valeurs qui ne la vérifient pas
 *   dans −10..10 ; degré 1 (le degré 2 est le niveau 5, désactivé par défaut).
 *   Exclusions appliquées : aucune seconde variable ; la valeur qui vérifie
 *   l'égalité est toujours entière (elle est tirée d'abord, la constante du
 *   second membre en découle) ; coefficients des deux membres différents ;
 *   aux niveaux 1 à 3, les valeurs testées sont distinctes et de conclusions
 *   opposées ; aucune constante nulle ; jamais deux membres écrits à
 *   l'identique ; aucune résolution demandée.
 *   Erreurs visées → mots-clés :
 *     résoudre au lieu de tester          → resoudre_au_lieu_de_tester
 *     un seul essai réussi vaut preuve    → un_seul_essai_reussi
 *     un seul essai raté vaut réfutation  → un_seul_essai_rate
 *     parenthèses du négatif oubliées     → parentheses_du_negatif_oubliees
 *     seules les constantes comparées     → constantes_seules_comparees
 *     un seul membre substitué            → un_seul_membre_substitue
 *     (second degré : une seule solution) → NON SERVI (niveau 5 désactivé)
 *     (identité : croire s'être trompé)   → NON SERVI (niveau 6 désactivé)
 *   Graduation → cartes : 1 → croiser_* (tout positif), vf_tableau_vf,
 *   compar_* ; 2 → croiser_*, vf_tableau_vf (constantes de signe quelconque) ;
 *   3 → croiser_*, compar_* (valeurs testées de signe quelconque) ;
 *   4 → croiser_texte_trou_saisie (trois valeurs, contrainte de conclusions
 *   opposées levée) ; 5 et 6 → aucune carte, « désactivés par défaut ».
 *   Non tranché : le `type_reponse` veut les champs numériques ET la conclusion
 *   dans UNE question notée pour un point entier ; aucune fabrique du dépôt ne
 *   le permet, les deux formats sont deux cartes de la même rubrique.
 *
 * Arithmétique entière exacte : la valeur qui vérifie l'égalité est tirée, les
 * constantes en découlent — aucune division, donc aucune solution fractionnaire.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 934;

const L_CROISER = 'A = B ? Tester pour deux valeurs';
const L_VF = 'Vrai ou faux pour la valeur indiquée';
const L_COMPAR = 'Comparer deux expressions dans un tableau';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOINS = '−';   // U+2212 (affichage uniquement, jamais en saisie)

// ─── Bornes de la spécification ─────────────────────────────────────────
const COEF_MIN = 1;
const COEF_MAX = 9;
const CST_MIN = -9;
const CST_MAX = 9;
const VAL_MAX = 10;   // les valeurs testées vivent dans [−VAL_MAX ; VAL_MAX]

/** Affichage d'un relatif : le vrai signe moins (U+2212). */
const aff = (v) => String(v).replace('-', MOINS);
/** Un membre « ax + b » correctement écrit (jamais « 1x », jamais « + −3 »). */
const membre = (a, b) => {
    const partieX = a === 1 ? 'x' : `${a}x`;
    return `${partieX} ${b < 0 ? MOINS : '+'} ${Math.abs(b)}`;
};
const evalue = (a, b, x) => a * x + b;

/**
 * Une lettre de variable s'écrit en italique mathématique, jamais en lettre
 * ordinaire (règle d'équipe, retour prof 2026-09-24). Trois rendus, trois écritures :
 *   - ÉNONCÉ (`payload.texte`) : la regex de rendu est STRICTE, le fragment doit
 *     commencer par une commande LaTeX → `tex()` pose le `\mathrm{}` de largeur nulle ;
 *   - PHRASE À TROUS mixte, CHOIX de QCM, lignes de vrai/faux : regex PERMISSIVE →
 *     le « $x$ » nu suffit, et `\mathrm{}` y est interdit.
 * `enLatex` traduit notre écriture d'AFFICHAGE en LaTeX (le moins typographique
 * notamment). Les membres restent stockés BRUTS : ils sont comparés et dédoublonnés.
 *
 * ⚠️ Dans une phrase à trous, ne jamais produire une ligne qui COMMENCE et FINIT par
 * « $ » : `hasMathLines` ferait basculer toute la phrase en mode KaTeX global.
 */
const enLatex = (s) => String(s)
    .replace(/²/g, '^2')
    .replace(/−/g, '-')
    .replace(/\s*×\s*/g, ' \\times ')
    .replace(/(\d),(\d)/g, '$1{,}$2');
const lettreEnonce = (L) => tex(L);
const lettreTrou = (L) => `$${L}$`;
const mathEnonce = (s) => tex(enLatex(s));
const mathChoix = (s) => `$${enLatex(s)}$`;

/** Une constante non nulle dans les bornes (exclusions 5). */
const tirerConstante = (positive) => {
    const v = randInt(1, CST_MAX);
    return positive ? v : v * randomChoice([1, -1]);
};

/**
 * Le cœur du tirage : on choisit l'écart des coefficients et la valeur qui
 * vérifie l'égalité, PUIS les constantes. La valeur est entière par
 * construction (exclusions 2) et les constantes tiennent dans leurs bornes.
 * Rend null quand le tirage ne tient pas.
 */
const tirerEgalite = (niveau, xMax) => {
    const delta = randomChoice([-3, -2, -1, 1, 2, 3]);
    const a2 = randInt(Math.max(COEF_MIN, COEF_MIN - delta), Math.min(COEF_MAX, COEF_MAX - delta));
    const a1 = a2 + delta;
    if (a1 < COEF_MIN || a1 > COEF_MAX || a1 === a2) return null;
    const ampleur = Math.min(xMax, Math.floor((CST_MAX - 1) / Math.abs(delta)));
    if (ampleur < 1) return null;
    const xStar = niveau <= 2 ? randInt(1, ampleur) : randInt(1, ampleur) * randomChoice([1, -1]);
    const b1 = tirerConstante(niveau === 1);
    const b2 = b1 + delta * xStar;
    if (b2 === 0 || b2 < CST_MIN || b2 > CST_MAX) return null;
    if (niveau === 1 && b2 < 1) return null;
    return { a1, b1, a2, b2, xStar };
};

// ═════════ Carte 1 — A = B ? (niveaux 1 à 4) ═════════

/**
 * A = a1·x + b1 et B = a2·x + b2, coefficients DIFFÉRENTS (exclusions 3).
 * La valeur qui vérifie l'égalité est tirée d'abord et b2 en découle :
 * b2 = b1 + (a1 − a2)·x*, donc x* est entier par construction (exclusions 2).
 */
const tirerCroisement = (niveauImpose) => {
    for (let essai = 0; essai < 200; essai++) {
        const niveau = niveauImpose || randomChoice([1, 2, 3, 4]);
        const base = tirerEgalite(niveau, VAL_MAX);
        if (!base) continue;
        const { a1, b1, a2, b2, xStar } = base;
        const nbValeurs = niveau === 4 ? 3 : 2;
        const plage = niveau <= 2
            ? Array.from({ length: VAL_MAX }, (_, i) => i + 1)
            : Array.from({ length: 2 * VAL_MAX + 1 }, (_, i) => i - VAL_MAX);
        let valeurs;
        if (niveau <= 3) {
            // exclusions 4 : conclusions opposées obligatoires
            const autres = shuffle(plage.filter(v => v !== xStar)).slice(0, nbValeurs - 1);
            if (autres.length < nbValeurs - 1) continue;
            valeurs = shuffle([xStar, ...autres]);
        } else {
            // niveau 4 : la contrainte est levée, il arrive qu'aucune ne convienne
            const avecStar = randomChoice([true, false]);
            const autres = shuffle(plage.filter(v => v !== xStar)).slice(0, avecStar ? nbValeurs - 1 : nbValeurs);
            if (autres.length < (avecStar ? nbValeurs - 1 : nbValeurs)) continue;
            valeurs = shuffle(avecStar ? [xStar, ...autres] : autres);
        }
        return { niveau, a1, b1, a2, b2, xStar, valeurs, nbValeurs };
    }
    return {
        niveau: 1, a1: 2, b1: 3, a2: 1, b2: 5, xStar: 2, valeurs: [2, 4], nbValeurs: 2,
    };
};

const genCroiserTrous = () => {
    const t = tirerCroisement();
    const lignes = t.valeurs.map(v =>
        `Pour ${lettreTrou('x')} = ${aff(v)} : A = {{${evalue(t.a1, t.b1, v)}}} `
        + `et B = {{${evalue(t.a2, t.b2, v)}}}`);
    const payload = makeTexteTrou(lignes.join('\n'),
        `<p><b>On considère A = ${mathEnonce(membre(t.a1, t.b1))} `
        + `et B = ${mathEnonce(membre(t.a2, t.b2))}.</b> `
        + `Calcule A et B pour chaque valeur de ${lettreEnonce('x')}, chaque membre de son côté. `
        + 'Mets la valeur entre parenthèses quand elle est négative.</p>',
        { ...opts(L_CROISER), mode: 'saisie' });
    payload._meta.params = {
        ...t, nbVariables: 1,
        erreurVisee: [
            'resoudre_au_lieu_de_tester',
            'un_seul_membre_substitue',
            ...(t.valeurs.some(v => v < 0) ? ['parentheses_du_negatif_oubliees'] : []),
        ],
    };
    return payload;
};

const genCroiserQcm = () => {
    const t = tirerCroisement(randomChoice([1, 2, 3]));
    const [v1, v2] = t.valeurs;
    const aucune = !t.valeurs.includes(t.xStar);
    const x = lettreTrou('x');
    const correct = aucune
        ? 'Pour aucune des deux valeurs testées.'
        : `Pour ${x} = ${aff(t.xStar)} seulement.`;
    const autre = t.xStar === v1 ? v2 : v1;
    const candidats = [
        { valeur: `Pour ${x} = ${aff(autre)} seulement.`, code: 'un_seul_membre_substitue' },
        { valeur: 'Pour les deux valeurs testées.', code: 'un_seul_essai_reussi' },
        { valeur: 'Pour aucune des deux valeurs testées.', code: 'un_seul_essai_rate' },
    ].filter(c => c.valeur !== correct);
    const choix = [correct, ...candidats.map(c => c.valeur)];
    const payload = makeQcm(choix, correct,
        `<p><b>A = ${mathEnonce(membre(t.a1, t.b1))} et B = ${mathEnonce(membre(t.a2, t.b2))}.</b> `
        + `Calcule A et B pour ${lettreEnonce('x')} = ${aff(v1)}, `
        + `puis pour ${lettreEnonce('x')} = ${aff(v2)}. `
        + 'Pour quelle(s) valeur(s) testée(s) a-t-on A = B ?</p>',
        opts(L_CROISER));
    payload._meta.params = { ...t, nbVariables: 1, distracteurs: candidats };
    return payload;
};

// ═════════ Carte 2 — Vrai ou faux pour x donné (niveaux 1 et 2) ═════════

/**
 * Trois familles de lignes, toutes du premier degré :
 *  - 'lineaire'    : a1x + b1 = a2x + b2 pour x = v, vraie ou fausse au choix ;
 *  - 'distributif' : k(x + m) = kx + m, TOUJOURS fausse (k ≥ 2, m ≥ 1) ;
 *  - 'constantes'  : a1x + b = a2x + b pour x = v, constantes ÉGALES et
 *    coefficients différents — toujours fausse, et elle piège exactement
 *    l'élève qui ne compare que les constantes (erreurs_visees 5).
 */
const tirerLigneVf = (vraieVoulue, niveau) => {
    const famille = vraieVoulue
        ? 'lineaire'
        : randomChoice(['lineaire', 'distributif', 'constantes']);
    // valeur testée petite : la constante du second membre doit rester dans ses bornes
    const v = randInt(1, 5);
    if (famille === 'lineaire') {
        const base = tirerEgalite(niveau, 6);
        if (!base) return null;
        const { a1, b1, a2 } = base;
        // la ligne est éprouvée pour la valeur v : vraie si v est la valeur qui
        // vérifie l'égalité, fausse sinon — on décale la constante pour le cas faux
        const b2 = vraieVoulue
            ? b1 + (a1 - a2) * v
            : b1 + (a1 - a2) * v + randomChoice([1, -1, 2, -2]);
        if (b2 === 0 || b2 < CST_MIN || b2 > CST_MAX) return null;
        if (niveau === 1 && b2 < 1) return null;
        return {
            ligne: `${mathChoix(`${membre(a1, b1)} = ${membre(a2, b2)}`)} pour ${lettreTrou('x')} = ${v}`,
            vraie: evalue(a1, b1, v) === evalue(a2, b2, v),
            code: vraieVoulue ? null : 'un_seul_essai_reussi',
        };
    }
    if (famille === 'distributif') {
        const k = randInt(2, COEF_MAX);
        const m = randInt(2, CST_MAX);
        return {
            ligne: `${mathChoix(`${k}(x + ${m}) = ${k}x + ${m}`)} pour ${lettreTrou('x')} = ${v}`,
            vraie: false,
            code: 'resoudre_au_lieu_de_tester',
        };
    }
    const a1 = randInt(COEF_MIN, COEF_MAX);
    const a2 = randInt(COEF_MIN, COEF_MAX);
    if (a1 === a2) return null;
    const b = tirerConstante(niveau === 1);
    return {
        ligne: `${mathChoix(`${membre(a1, b)} = ${membre(a2, b)}`)} pour ${lettreTrou('x')} = ${v}`,
        vraie: false,
        code: 'constantes_seules_comparees',
    };
};

const genVf = () => {
    const niveau = randomChoice([1, 2]);
    const lignes = [];
    const bonnes = [];
    const codes = [];
    const vues = new Set();
    const nbVrai = randInt(1, 3);
    const voulues = shuffle([
        ...Array(nbVrai).fill(true),
        ...Array(4 - nbVrai).fill(false),
    ]);
    voulues.forEach((vraieVoulue, iv) => {
        let ajoutee = false;
        for (let essai = 0; essai < 60; essai++) {
            const t = tirerLigneVf(vraieVoulue, niveau);
            if (!t || t.vraie !== vraieVoulue) continue;   // garde : vérité recalculée
            if (vues.has(t.ligne)) continue;
            vues.add(t.ligne);
            lignes.push(t.ligne);
            bonnes.push(t.vraie ? 0 : 1);
            if (t.code) codes.push(t.code);
            ajoutee = true;
            break;
        }
        if (!ajoutee) {
            // repli déterministe, conforme à la vérité voulue et distinct (k varie)
            const k = 2 + iv;
            const ligne = vraieVoulue
                ? `${mathChoix(`${k}x + 1 = ${k + 1}x ${MOINS} 1`)} pour ${lettreTrou('x')} = 2`
                : `${mathChoix(`${k}(x + 7) = ${k}x + 7`)} pour ${lettreTrou('x')} = 5`;
            if (!vues.has(ligne)) {
                vues.add(ligne);
                lignes.push(ligne);
                bonnes.push(vraieVoulue ? 0 : 1);
                if (!vraieVoulue) codes.push('resoudre_au_lieu_de_tester');
            }
        }
    });
    const payload = makeVraiFaux(lignes, bonnes,
        `<p><b>Vrai ou faux ?</b> Pour chaque ligne, remplace ${lettreEnonce('x')} par la valeur indiquée `
        + 'et calcule les deux membres, chacun de son côté. On conclut pour cette valeur '
        + 'seulement.</p>',
        opts(L_VF));
    payload._meta.params = {
        lignes, bonnes, niveau, nbVariables: 1, nbValeurs: 2,
        erreurVisee: [...new Set([...codes, 'un_seul_essai_reussi', 'un_seul_essai_rate'])],
    };
    return payload;
};

// ═════════ Carte 3 — Tableau comparatif (niveaux 1 à 3) ═════════

const T_TH = 'background:#1E3A8A;color:#fff;padding:6px 14px;border:1px solid #64748b;font-weight:600';
const T_TD = 'padding:6px 14px;border:1px solid #64748b;text-align:center';
const ligneTable = (th, cells) => `<tr><th style="${T_TH}">${th}</th>`
    + cells.map((c) => `<td style="${T_TD}">${c}</td>`).join('') + '</tr>';
const tableEnonce = (...lignes) =>
    `<table style="border-collapse:collapse;margin:12px auto">${lignes.join('')}</table>`;

/**
 * Deux expressions du premier degré comparées sur trois valeurs. La valeur qui
 * vérifie l'égalité appartient parfois au tableau, parfois non : la conclusion
 * tourne (« vraie pour cette valeur seulement » / « aucune des valeurs testées »).
 */
const tirerComparaison = () => {
    for (let essai = 0; essai < 200; essai++) {
        const niveau = randomChoice([1, 2, 3]);
        const base = tirerEgalite(niveau, 6);
        if (!base) continue;
        const { a1, b1, a2, b2, xStar } = base;
        const avecStar = randomChoice([true, false]);
        const plage = niveau <= 2
            ? [1, 2, 3, 4, 5, 6]
            : [-3, -2, -1, 1, 2, 3, 4, 5];
        const autres = shuffle(plage.filter(v => v !== xStar)).slice(0, avecStar ? 2 : 3);
        if (autres.length < (avecStar ? 2 : 3)) continue;
        const xs = (avecStar ? [xStar, ...autres] : autres).sort((u, w) => u - w);
        return {
            niveau, a1, b1, a2, b2, xStar, xs, avecStar,
            e1: { txt: membre(a1, b1), f: (x) => evalue(a1, b1, x) },
            e2: { txt: membre(a2, b2), f: (x) => evalue(a2, b2, x) },
        };
    }
    return {
        niveau: 1, a1: 2, b1: 3, a2: 1, b2: 5, xStar: 2, xs: [1, 2, 4], avecStar: true,
        e1: { txt: '2x + 3', f: (x) => 2 * x + 3 },
        e2: { txt: 'x + 5', f: (x) => x + 5 },
    };
};

const tableComparaison = (t, trous1 = [], trous2 = []) => tableEnonce(
    ligneTable(lettreEnonce('x'), t.xs.map(aff)),
    ligneTable(`E₁ = ${mathEnonce(t.e1.txt)}`, t.xs.map((x, i) => (trous1.includes(i) ? '…' : aff(t.e1.f(x))))),
    ligneTable(`E₂ = ${mathEnonce(t.e2.txt)}`, t.xs.map((x, i) => (trous2.includes(i) ? '…' : aff(t.e2.f(x))))),
);

const genComparTrous = () => {
    const t = tirerComparaison();
    const idx = t.xs.map((_, i) => i);
    const trous1 = shuffle(idx).slice(0, 2).sort();
    const trous2 = shuffle(idx).slice(0, 2).sort();
    // Une case par ligne (l'impression coupait une ligne portant deux trous).
    const x = lettreTrou('x');
    const phrase = [
        ...trous1.map(i => `Pour ${x} = ${aff(t.xs[i])}, E₁ = {{${t.e1.f(t.xs[i])}}}`),
        ...trous2.map(i => `Pour ${x} = ${aff(t.xs[i])}, E₂ = {{${t.e2.f(t.xs[i])}}}`),
    ].join('\n');
    const payload = makeTexteTrou(phrase,
        `<p><b>On compare E₁ = ${mathEnonce(t.e1.txt)} et E₂ = ${mathEnonce(t.e2.txt)}.</b> `
        + 'Complète le tableau : chaque membre se calcule de son côté.</p>\n'
        + tableComparaison(t, trous1, trous2),
        { ...opts(L_COMPAR), mode: 'saisie' });
    payload._meta.params = {
        ...t, e1: t.e1.txt, e2: t.e2.txt, trous1, trous2, nbVariables: 1,
        nbValeurs: t.xs.length,
        erreurVisee: ['un_seul_membre_substitue', 'resoudre_au_lieu_de_tester'],
    };
    return payload;
};

const genComparQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const t = tirerComparaison();
        const xNomme = t.avecStar ? t.xStar : t.xs[0];
        const x = lettreTrou('x');
        const O1 = `L'égalité E₁ = E₂ n'est vraie que pour ${x} = ${aff(xNomme)}.`;
        const O2 = 'Aucune des valeurs testées ne vérifie l\'égalité E₁ = E₂.';
        const O3 = `E₁ = E₂ pour toute valeur de ${x}.`;
        const O4 = `E₁ et E₂ ne sont égales pour aucune valeur de ${x}, testée ou non.`;
        const correct = t.avecStar ? O1 : O2;
        const candidats = [
            { valeur: t.avecStar ? O2 : O1, code: t.avecStar ? 'un_seul_essai_rate' : 'un_seul_essai_reussi' },
            { valeur: O3, code: 'un_seul_essai_reussi' },
            { valeur: O4, code: 'un_seul_essai_rate' },
        ];
        const choix = [correct, ...candidats.map(c => c.valeur)];
        if (new Set(choix).size !== choix.length) continue;
        const payload = makeQcm(choix, correct,
            `<p><b>On a comparé E₁ = ${mathEnonce(t.e1.txt)} et E₂ = ${mathEnonce(t.e2.txt)} `
            + 'dans le tableau ci-dessous.</b> '
            + 'Que peut-on en conclure ?</p>\n' + tableComparaison(t),
            opts(L_COMPAR));
        payload._meta.params = {
            ...t, e1: t.e1.txt, e2: t.e2.txt, nbVariables: 1, nbValeurs: t.xs.length,
            distracteurs: candidats,
        };
        return payload;
    }
    const t = tirerComparaison();
    const candidats = [
        { valeur: 'Aucune des valeurs testées ne vérifie l\'égalité E₁ = E₂.', code: 'un_seul_essai_rate' },
        { valeur: `E₁ = E₂ pour toute valeur de ${lettreTrou('x')}.`, code: 'un_seul_essai_reussi' },
        {
            valeur: `E₁ et E₂ ne sont égales pour aucune valeur de ${lettreTrou('x')}, testée ou non.`,
            code: 'un_seul_essai_rate',
        },
    ];
    const correct = `L'égalité E₁ = E₂ n'est vraie que pour ${lettreTrou('x')} = ${aff(t.xStar)}.`;
    const payload = makeQcm([correct, ...candidats.map(c => c.valeur)], correct,
        `<p><b>On a comparé E₁ = ${mathEnonce(t.e1.txt)} et E₂ = ${mathEnonce(t.e2.txt)} `
        + 'dans le tableau ci-dessous.</b> '
        + 'Que peut-on en conclure ?</p>\n' + tableComparaison(t),
        opts(L_COMPAR));
    payload._meta.params = {
        ...t, e1: t.e1.txt, e2: t.e2.txt, nbVariables: 1, nbValeurs: t.xs.length,
        distracteurs: candidats,
    };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    croiser_texte_trou_saisie: genCroiserTrous,
    croiser_qcm_unique: genCroiserQcm,
    vf_tableau_vf: genVf,
    compar_texte_trou_saisie: genComparTrous,
    compar_qcm_unique: genComparQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'croiser', label: L_CROISER, formatsList: buildFormats('croiser') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
    { id: 'compar', label: L_COMPAR, formatsList: buildFormats('compar') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
